import { Injectable } from '@angular/core';
import {Observable} from 'rxjs';
import {ConfigService} from '../../../core/services/config.service';
import {RequestService} from '../../../core/services/request.service';
import {Certificate, CertificateDict} from './models';
import {AmberApi} from '../../../core/models/api-helpers';
import { DateTime } from 'aws-sdk/clients/devicefarm';
import {DynamicGridField} from '../../../shared/components/dynamic-grid/dynamic-grid.component';

declare var moment: any;

@Injectable()
export class CertificatesService {

    constructor(
        private configService: ConfigService,
        private requestService: RequestService
    ) { }

    private formatDate(date: any) {
        return date ? moment(new Date(date)).format('D MMM YYYY') : 'N/A';
    }

    getCertificatesGridFields(): any[] {
        return [
            {
                title: 'Name',
                name: 'displayName',
                sortable: true
            },
            {
                title: 'Creation Date',
                name: 'createdAt',
                sortable: true
            },
            {
                title: 'Validity',
                name: 'validityPeriod',
                sortable: true
            },
            {
                title: 'Last Updated',
                name: 'updatedAt',
                sortable: true
            },
            {
                title: 'Status',
                name: 'status.configValue',
                sortable: true
            }
        ];
    }

    certificatesIssuedFields(): DynamicGridField[] {
        return [
            {
                title: 'Certificate ID',
                name: 'id',
                sortable: true,
                weight: 3
            },
            {
                title: 'Recipient',
                name: 'participateName',
                sortable: true,
                weight: 1
            },
            {
                title: 'Issuing Date',
                name: 'createdAt',
                sortable: true,
                weight: 2,
                formatter: (date) => this.formatDate(date)
            },
            {
                title: 'Expiration Date',
                name: 'expirationDate',
                sortable: true,
                weight: 2,
                formatter: (date) => this.formatDate(date)
            },
            {
                title: '',
                name: 'download',
                config: {
                    type: 'template',
                    template: 'downloadCertificate'
                },
                sortable: false,
                weight: 2
            }
            
        ];
    }

    getCertificateAvailableFields() {
        let detailsRoute = '/organization/certificates/{{key}}/details'

        let availableFields = [
            {
                title: 'Name',
                name: 'displayName',
                config: {
                    type: 'link',
                    route: detailsRoute,
                    key: 'id'
                },
                sortable: true,
                weight: 3
            },
            {
                title: 'Creation Date',
                name: 'createdAt',
                sortable: true,
                weight: 2,
                formatter: (date) => this.formatDate(date)
            },
            {
                title: 'Validity',
                name: 'validityPeriod',
                sortable: true,
                formatter: (validity) => {
                    return validity ? `${validity} ${validity==1 ? 'Month' : 'Months'}` : 'No Expiration';
                }
            },
            {
                title: 'Last Updated',
                name: 'updatedAt',
                sortable: true,
                weight: 2,
                formatter: (date) => this.formatDate(date)
            },
            {
                title: 'Status',
                name: 'status.configValue',
                sortable: true
            }
        ];
        return availableFields;
    }

    getCertificateDetails(id: string): Observable<Certificate> {
        const url = this.configService.get('server').url + this.configService.get('endpoints').organization.certificates.details.api + '/' + id;
        return this.requestService.get(url).extract(AmberApi.Data);
    }

    getCertificateDetailsBySKU(sku: string): Observable<Certificate> {
        const url = this.configService.get('server').url + this.configService.get('endpoints').organization.certificates.detailsBySKU.api + '/' + sku;
        return this.requestService.get(url).extract(AmberApi.Data);
    }

    createCertificate(certificate: Certificate): Observable<Certificate> {
        const url = this.configService.get('server').url + this.configService.get('endpoints').organization.certificates.add.api;
        return this.requestService.post(url, certificate).extract(AmberApi.Data);
    }

    editCertificate(certificate: Certificate): Observable<Certificate> {
        const url = this.configService.get('server').url + this.configService.get('endpoints').organization.certificates.edit.api;
        return this.requestService.post(url, certificate).extract(AmberApi.Data);
    }

    validateCertificate(key: string, bucket: string): Observable<any> {
        const body = {
            s3Key: key,
            s3Bucket: bucket
        }
        const url = this.configService.get('server').url + this.configService.get('endpoints').organization.certificates.validate.api;
        return this.requestService.post(url, body, true).extract(AmberApi.Data);
    }

    toggleStatusCertificate(certificate: Certificate, newId: string = ''): Observable<Certificate> {
        const body = {
            id: certificate.id,
            sku: certificate.sku,
            s3Bucket: certificate.s3Bucket,
            s3Key: certificate.s3Key
        }
        const url = this.configService.get('server').url + this.configService.get('endpoints').organization.certificates.toggleStatus.api + (!newId ? '' : `?id=${newId}`);
        return this.requestService.post(url, body).extract(AmberApi.Data);
    }

    getCertificateDictionary(): Observable<CertificateDict[]> {
        const url = this.configService.get('server').url + this.configService.get('endpoints').organization.certificates.certDictionary.api;
        return this.requestService.get(url).extract(AmberApi.Data);
    }

    getCertificatesLinkedCoursesURL(certificateId: string): string {
        return this.configService.get('server').url +
                this.configService.get('endpoints').solar.course.linkedCourses.api +
                 '?id=' + certificateId;
    }

    getCourseCategoriesByCertificate(certificateId: string): Observable<{key: string, value: string}[]> {
        const url = this.configService.get('server').url +
                     this.configService.get('endpoints').solar.course.courseCategoriesByCertificate.api +
                      '?certificateId=' + certificateId;
        return this.requestService.get(url).extract(AmberApi.Data);
    }

    getCertificatesLinkedProductsURL(certificateId: string): string {
        return this.configService.get('server').url +
            this.configService.get('endpoints').solar.course.linkedProducts.api +
            '?certificateId=' + certificateId;
    }

    getProductCategoriesByCertificate(certificateId: string): Observable<{key: string, value: string}[]> {
        const url = this.configService.get('server').url +
            this.configService.get('endpoints').solar.course.productCategoriesByCertificate.api +
            '?certificateId=' + certificateId;
        return this.requestService.get(url).extract(AmberApi.Data);
    }

    getProductDetailsBySKU(sku: string): Observable<any> {
        const url = this.configService.get('server').url +
            this.configService.get('endpoints').agora.products.detailsBySKU.api +
            '/' + sku;
        return this.requestService.get(url).extract(AmberApi.Data);
    }
    
    getCertificatesIssued(certificateId: string, issueDate: any, expirationDate: any): string {
        let url = 
            this.configService.get('server').url + 
            this.configService.get('endpoints').solar.classevent.certificate.certificatesIssued.api
            + '?certificateId=' + certificateId;
        if(issueDate) {
            url += '&issuingDate=' + issueDate;
        }
        if(expirationDate) {
            url += '&expirationDate=' + expirationDate;
        }
        return url;
    }
    certificateNameCheck(name:string){
        let url = 
            this.configService.get('server').url + 
            this.configService.get('endpoints').solar.classevent.certificate.certificateNameCheck.api
            + '?name=' + name;
           return this.requestService.get(url).extract(AmberApi.Data);   
    }
}



// WEBPACK FOOTER //
// ./src/app/organization/components/certificates/certificates.service.ts